/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect } from "react";
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Text,
} from "@chakra-ui/react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import axios from "axios";

import { useToastPromise } from "@/hooks/useToast";
import EditorialOfficeForm from "../Signup/EditorialOfficeForm";
import { mutateSession, useUser } from "@/hooks/useUser";
import { mutate } from "swr";

type Inputs = {
  name?: string;
  url?: string;
  officeEmail?: string;
  phone?: string;
  position?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  country?: string;
  type?: string;
};

type Props = {
  id?: number;
  isOpen: boolean;
  onClose: (id?: number) => void;
  defaultValues?: Inputs;
  onSubmit?: ({
    success,
    error,
    id,
  }: {
    success?: boolean;
    error?: any;
    id?: number;
  }) => void;
  withRequest?: true;
};

const EditorialOfficeModal = ({
  isOpen,
  onClose,
  defaultValues,
  onSubmit: onPlaceFormSubmit,
  withRequest,
}: Props): React.ReactElement => {
  const methods = useForm();
  const {
    handleSubmit,
    formState: { isSubmitting },
    reset,
    watch,
  } = methods;
  const { t } = useTranslation("common");
  const toast = useToastPromise();
  const authorizedPerson = watch("authorizedPerson");
  const [session] = useUser();

  const onSubmit = (data) => {
    return toast.promise(
      axios
        .post("/editorial-office/register", {
          ...data,
          representative: authorizedPerson
            ? `${session?.user?.firstName} ${session?.user?.lastName}`
            : data?.representative,
          formType: !authorizedPerson ? "suggest" : null,
          message: withRequest && !authorizedPerson ? data?.message : null,
        })
        .then(async (response) => {
          await mutate("/accreditation/office/list");
          if (onPlaceFormSubmit) {
            onPlaceFormSubmit({ success: true, id: response.data.id });
          }

          if (authorizedPerson) mutateSession();

          onClose();
        })
        .catch((error) => {
          if (onPlaceFormSubmit) {
            onPlaceFormSubmit({ error });
          }
        })
    );
  };

  useEffect(() => {
    if (isOpen) {
      reset(defaultValues);
    }
  }, [isOpen]);

  return (
    <Modal
      size="xl"
      motionPreset="slideInBottom"
      isOpen={isOpen}
      onClose={onClose}
      preserveScrollBarGap
    >
      <ModalOverlay>
        <ModalContent minW="650px">
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <ModalHeader>
                {t("editorialOffice.suggestEditorialOffice")}
                {!authorizedPerson && (
                  <>
                    <Text
                      mt={3}
                      style={{ fontWeight: "normal", textAlign: "justify" }}
                    >
                      {t("editorialOffice.suggestEditorialOfficeInformation1")}
                    </Text>
                    <Text
                      mt={3}
                      style={{ fontWeight: "normal", textAlign: "justify" }}
                    >
                      {t("editorialOffice.suggestEditorialOfficeInformation2")}
                    </Text>
                  </>
                )}
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <EditorialOfficeForm panel suggest withRequest={withRequest} />
              </ModalBody>

              <ModalFooter>
                <Button variant="ghost" onClick={() => onClose()}>
                  {t("cancel")}
                </Button>
                <Button type="submit" isLoading={isSubmitting} ml={3}>
                  {t("send")}
                </Button>
              </ModalFooter>
            </form>
          </FormProvider>
        </ModalContent>
      </ModalOverlay>
    </Modal>
  );
};

export default EditorialOfficeModal;
